import React from 'react';
import { useLocation } from 'react-router-dom';

const StructuredData = () => {
  const location = useLocation();
  
  // Organization Schema (immer auf allen Seiten)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://daniel-leismann.de/#organization",
    "name": "Daniel Leismann - Digitale Prozessoptimierung",
    "url": "https://daniel-leismann.de",
    "logo": "https://daniel-leismann.de/logo.png",
    "image": "https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg",
    "description": "Digitale Geschäftsprozesse, Buchhaltung und Vertrieb optimieren: KI, Automatisierung, APIs, Amazon/eBay, DATEV/LexOffice. Keine Vorkosten – Sie zahlen nur aus echten Einsparungen.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Köln",
      "addressRegion": "NRW",
      "addressCountry": "DE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 50.937531,
      "longitude": 6.960279
    },
    "email": "mail@daniel-leismann.de",
    "telephone": "+49-123-456789",
    "priceRange": "€€",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "10:00",
        "closes": "17:00"
      }
    ],
    "sameAs": [
      "https://www.linkedin.com/in/daniel-leismann"
    ],
    "areaServed": {
      "@type": "Country",
      "name": "Deutschland"
    },
    "serviceType": [
      "Prozessoptimierung",
      "Digitale Transformation",
      "Buchhaltungsautomatisierung",
      "E-Commerce Optimierung",
      "Custom Software Development"
    ]
  };

  // Person Schema für Daniel Leismann
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://daniel-leismann.de/#person",
    "name": "Daniel Leismann",
    "jobTitle": "Berater für Digitale Prozessoptimierung",
    "email": "mail@daniel-leismann.de",
    "image": "https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg",
    "worksFor": {
      "@id": "https://daniel-leismann.de/#organization"
    },
    "knowsAbout": [
      "Prozessautomatisierung",
      "Python Programmierung",
      "DATEV Integration",
      "LexOffice Optimierung",
      "Amazon FBA",
      "E-Commerce Multichannel",
      "KI-Integration",
      "API-Entwicklung"
    ]
  };

  // Website Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://daniel-leismann.de/#website",
    "url": "https://daniel-leismann.de",
    "name": "Daniel Leismann - Digitale Prozessoptimierung",
    "description": "Digitale Geschäftsprozesse optimieren. Keine Vorkosten – Sie zahlen nur aus echten Einsparungen.",
    "publisher": {
      "@id": "https://daniel-leismann.de/#organization"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://daniel-leismann.de/?s={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://daniel-leismann.de"
      }
    ]
  };

  // Service Schemas für einzelne Leistungsseiten
  const serviceSchemas = {
    "/digitale-buchhaltung": {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Digitale Buchhaltung",
      "provider": { "@id": "https://daniel-leismann.de/#organization" },
      "name": "Digitale Buchhaltung optimieren – LexOffice, DATEV, Addison",
      "description": "Digitalisierung der Buchhaltungsprozesse, Automatisierung der Rechnungsstellung, nahtlose Schnittstellen zwischen allen Systemen.",
      "areaServed": "Deutschland",
      "offers": {
        "@type": "Offer",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "EUR",
          "price": "0",
          "description": "Keine Vorkosten - Bezahlung nur aus Einsparungen"
        }
      }
    },
    "/prozessautomatisierung": {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Prozessautomatisierung",
      "provider": { "@id": "https://daniel-leismann.de/#organization" },
      "name": "Prozessautomatisierung – Workflows digitalisieren",
      "description": "Automatisierung von Workflows, API-Integration, Custom Scripts in Python für optimierte Geschäftsprozesse.",
      "areaServed": "Deutschland"
    },
    "/amazon-optimierung": {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Amazon Optimierung",
      "provider": { "@id": "https://daniel-leismann.de/#organization" },
      "name": "Amazon Optimierung – SEO, VCS Lite, FBA & Vendor Central",
      "description": "Optimierung von Amazon-Präsenz: Produkt-SEO, VCS Lite, FBA-Logistik und Vendor Central.",
      "areaServed": "Deutschland"
    },
    "/erstgespraech": {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Erstgespräch",
      "provider": { "@id": "https://daniel-leismann.de/#organization" },
      "name": "Kostenloses Erstgespräch",
      "description": "1-stündige kostenlose Erstanalyse Ihrer Prozesse mit konkreten Einsparpotentialen.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR"
      }
    }
  };

  // Aktualisiere Breadcrumb basierend auf der Route
  if (location.pathname !== '/') {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      "position": 2,
      "name": pathSegments[0].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      "item": `https://daniel-leismann.de${location.pathname}`
    });
  }

  // Service Schema für aktuelle Seite
  const currentServiceSchema = serviceSchemas[location.pathname];

  return (
    <>
      {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* Person Schema */}
      <script type="application/ld+json">
        {JSON.stringify(personSchema)}
      </script>

      {/* Website Schema */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      {/* Service Schema (wenn vorhanden) */}
      {currentServiceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(currentServiceSchema)}
        </script>
      )}
    </>
  );
};

export default StructuredData;
